import * as z from 'zod'
import { baseEntitySchema } from '../../core/entity.js'

export const calendarSchema = baseEntitySchema.extend({
  type: z.literal('calendar'),
})
export type Calendar = z.infer<typeof calendarSchema>

export function isCalendar(node?: unknown): node is Calendar {
  return calendarSchema.safeParse(node).success
}

const attendeeSchema = z.object({
  name: z.string(),
  userId: z.string(),
  /** Known only when a member of the calendar created the event: other users' profiles carry no email */
  email: z.string().email().describe('Attendee email, for calendar invites').optional(),
  optional: z.boolean().optional().default(false),
})

export type Attendee = z.infer<typeof attendeeSchema>

/**
 * One occurrence of a series changed on its own (GH #2814, spec R2), keyed by its original start. Cancelled, or with
 * the fields that differ from the series.
 */
export const occurrenceExceptionSchema = z.object({
  cancelled: z.literal(true).describe('The occurrence is cancelled').optional(),
  start: z.iso.datetime().describe('The moved start').optional(),
  end: z.iso.datetime().describe('The moved end').optional(),
  name: z.string().describe('A name for this occurrence only').optional(),
  location: z.string().describe('A location for this occurrence only').optional(),
})
export type OccurrenceException = z.infer<typeof occurrenceExceptionSchema>

export const calendarEventSchema = baseEntitySchema.extend({
  type: z.literal('calendarevent'),
  start: z.iso.datetime().nullable(),
  end: z.iso.datetime().nullable().optional(),
  allDay: z.boolean().default(false).optional(),
  location: z.string().optional(),
  organizer: attendeeSchema.optional(),
  attendees: z.array(attendeeSchema).optional(),
  /** GH #2814: an RFC 5545 RRULE without DTSTART (the event's start is it); occurrences are expanded on read (R1) */
  recurrence: z.string().describe('RRULE, e.g. FREQ=WEEKLY;BYDAY=TH').optional(),
  /** The organizer's IANA timezone: a series keeps its wall-clock time across DST. Missing: UTC */
  timezone: z.string().describe('IANA timezone, e.g. Europe/Oslo').optional(),
  /** Occurrences changed on their own, keyed by their original start (iCal RECURRENCE-ID, R2) */
  exceptions: z
    .record(z.iso.datetime(), occurrenceExceptionSchema)
    .describe('Per-occurrence changes, keyed by original start')
    .optional(),
  /** iCal SEQUENCE: increases with every change (R5) */
  sequence: z.number().int().min(0).describe('Revision, for iCal').optional(),
})
export type CalendarEvent = z.infer<typeof calendarEventSchema>

export function isCalendarEvent(node?: unknown): node is CalendarEvent {
  return calendarEventSchema.safeParse(node).success
}
